import { useApplicationStore } from '@/stores/application.store';
import { useUserStore } from '@/stores/user.store';
import { usePostHog } from '@/composables/usePostHog';
import authService from '@/services/auth.service';
import pageMetadataService from '@/services/page-metadata.service';
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import ubo from '@/router/ubo.router';
import stats from '@/router/stats.router';
import entity from '@/router/entity.router';
import payment from '@/router/payment.router';
import thankYou from '@/router/thank-you.router';
import document from '@/router/document.router';
import dashboard from '@/router/dashboard.router';
import uboFunnel from '@/router/ubo-funnel.router';
import authRouter from '@/router/auth.router';
import startRouter from '@/router/start.router';
import liquidation from '@/router/liquidation.router';
import generalFlow from '@/router/general-flow.router';
import userDetails from '@/router/user-details.router';
import loginRouter from '@/router/login.router';
import transactions from '@/router/transactions.router';
import organization from '@/router/organization.router';
import incorporation from '@/router/incorporation.router';
import adminSettings from '@/router/admin-settings.router';
import notifications from '@/router/notifications.router';
import processRouters from '@/router/process.router';
import generalFlowFunnel from '@/router/general-flow-funnel.router';
import liquidationFunnel from '@/router/liquidation-funnel.routes';
import incorporationFunnel from '@/router/incorporation-funnel.routes';
import bookkeepingOffer from '@/router/bookkeeping-offer.router';
import dgaSalaryAdminFunnel from '@/router/dga-salary-admin.router';
import jobs from '@/router/jobs.router';
import redirectRouter from './redirect.router';

import { routes as settingsRoutes } from '@/features/settings';
import { routes as checkoutRoutes } from '@/features/common/checkout';
import { routes as devRoutes } from '@/features/dev';

const routes: RouteRecordRaw[] = [
    {
        path: '',
        name: 'home',
        redirect: {
            name: 'login',
        },
    },
    {
        path: '/emails',
        name: 'support-emails',
        component: () => import('../components/Admin/AdminEmails/EmailsView.vue'),
        meta: {
            private: true,
            accountTypes: ['support'],
        },
    },
    {
        path: '/:catchAll(.*)',
        name: 'page404',
        component: () => import('../views/Global/Page404View.vue'),
    },
    payment,
    authRouter,
    adminSettings,
    loginRouter,
    startRouter,
    notifications,
    processRouters,
    document,
    dashboard,
    userDetails,
    organization,
    liquidation,
    incorporation,
    incorporationFunnel,
    liquidationFunnel,
    thankYou,
    transactions,
    stats,
    uboFunnel,
    ubo,
    generalFlowFunnel,
    generalFlow,
    entity,
    bookkeepingOffer,
    dgaSalaryAdminFunnel,
    jobs,
    redirectRouter,
    settingsRoutes,
    checkoutRoutes,
    // Dev-server only; empty in builds
    ...devRoutes,
];

const accountHasType = (account: any, to: any): boolean => {
    if (!to.meta.accountTypes && !to.matched.some((route) => route.meta.accountTypes)) return true;
    if (!account) return false;

    return to.meta.accountTypes?.includes(account.type) || to.matched.some((route) => route.meta.accountTypes?.includes(account.type));
};

const sessionHasRole = (session: any, to: any): boolean => {
    if (!to.meta.sessionRoles && !to.matched.some((route) => route.meta.sessionRoles)) return true;
    if (!session) return false;

    return to.meta.sessionRoles?.includes(session.role?.id) || to.matched.some((route) => route.meta.sessionRoles?.includes(session.role?.id));
};

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes,
    scrollBehavior() {
        return { top: 0, left: 0 };
    },
});

// Initialize PostHog
usePostHog();

router.beforeEach(async (to, _from, next) => {
    const userStore = useUserStore();
    const applicationStore = useApplicationStore();
    const isPrivate = to.meta.private || to.matched.some((route) => route.meta.private);
    const hasLocalSession = authService.hasLocalSession();
    const hasAccountType = accountHasType(userStore.session?.account, to);
    const hasSessionRole = sessionHasRole(userStore.session, to);

    if (hasLocalSession && (!hasAccountType || !hasSessionRole)) {
        return router.replace({ name: 'dashboard' });
    }

    pageMetadataService.setDefaultPageTitle(to);

    if ((to.name === 'login' || to.path.startsWith('/auth')) && hasLocalSession) {
        if (to.name !== 'logout') {
            return next({ name: 'dashboard' });
        }
    }

    if (!isPrivate) {
        return next();
    }

    if (to.name !== 'login' && !hasLocalSession && isPrivate) {
        applicationStore.updateDeepLink({
            name: to.name,
            params: to.params,
        });

        return next({ name: 'home' });
    } else {
        if (applicationStore.deepLink && applicationStore.deepLink.name && applicationStore.deepLink.name !== to.name) {
            next(applicationStore.deepLink);

            return applicationStore.updateDeepLink(null);
        }

        if (applicationStore.oAuthLink) {
            next(applicationStore.oAuthLink);

            applicationStore.updateOAuthLink(null);
        }

        return next();
    }
});

export default router;
